"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { TRANSFORMER_MAX_MOBILE_WIDTH_PX } from "../../../utils/constants.js";
import { mergeRefs } from "../../../utils/mergeRefs.js";
import { InputButton } from "../../InputButton/index.js";
import { DEFAULT_MAX_TRAVELERS } from "../constants.js";
import { useTravelerSelection } from "../hooks/useTravelerSelection.js";
import TravelerSelectionDesktop from "./TravelerSelectionDesktop.js";
import TravelerSelectionMobile from "./TravelerSelectionMobile.js";
function TravelerSelection({ ageSelectEnabled, disclaimer, errorMessage, initialValues, isInternationalFlight, isHotel = false, isTravelerSelectionDisabled = false, isWhiteLabel, lapInfantLabel, maxAdults, maxChildren, maxTravelers = DEFAULT_MAX_TRAVELERS, maxRooms, maxRoomsMessage, maxRoomsUrl, fireMaxRoomsOnClick, roomsEnabled, shouldCountLapInfant, subLabelMap, onChange, shouldAllowInternationalLapInfants, collisionAvoidance, inputRef, ...props }) {
    const useTravelerSelectionProps = useMemo(()=>({
            initialValues: {
                rooms: roomsEnabled ? 1 : 0,
                ...initialValues
            },
            ageSelectEnabled: !!ageSelectEnabled,
            errorMessage,
            isInternationalFlight: !!isInternationalFlight,
            isWhiteLabel,
            maxTravelers,
            shouldCountLapInfant,
            shouldAllowInternationalLapInfants
        }), [
        ageSelectEnabled,
        errorMessage,
        initialValues,
        isInternationalFlight,
        isWhiteLabel,
        maxTravelers,
        roomsEnabled,
        shouldAllowInternationalLapInfants,
        shouldCountLapInfant
    ]);
    const showDrawerOverlay = useMediaQuery({
        maxWidth: TRANSFORMER_MAX_MOBILE_WIDTH_PX
    });
    const [isOpen, setIsOpen] = useState(false);
    const state = useTravelerSelection(useTravelerSelectionProps);
    const setRoomsRef = useRef(state.setRooms);
    setRoomsRef.current = state.setRooms;
    useEffect(()=>{
        const rooms = roomsEnabled ? useTravelerSelectionProps.initialValues.rooms || 1 : 0;
        setRoomsRef.current(rooms);
    }, [
        roomsEnabled,
        useTravelerSelectionProps.initialValues.rooms
    ]);
    function handleOpen(val) {
        setIsOpen(val ?? true);
        if (onChange) onChange(state);
    }
    function handleClose() {
        setIsOpen(false);
        if (onChange) onChange(state);
    }
    const TravelerSelectionProps = {
        ageSelectEnabled,
        disclaimer,
        handleClose,
        isHotel,
        lapInfantLabel,
        maxAdults,
        maxChildren,
        maxRooms,
        fireMaxRoomsOnClick,
        maxRoomsMessage,
        maxRoomsUrl,
        roomsEnabled,
        shouldCountLapInfant,
        state,
        subLabelMap,
        onChange,
        ...props
    };
    const InputTrigger = useCallback(({ onClick, ref, ...props })=>/*#__PURE__*/ jsx(InputButton, {
            ...props,
            ref: mergeRefs(ref, inputRef),
            onClick: onClick,
            "data-testid": "traveler-selection-input",
            iconLeft: 'person',
            label: "Travelers",
            value: state.label,
            size: 'lg',
            className: "w-full",
            fullWidth: true,
            errorMessage: state.errorMessage,
            isDisabled: isTravelerSelectionDisabled,
            dataFocusWithin: isOpen
        }), [
        state.label,
        state.errorMessage,
        isTravelerSelectionDisabled,
        inputRef,
        isOpen
    ]);
    return showDrawerOverlay ? /*#__PURE__*/ jsxs(Fragment, {
        children: [
            InputTrigger({
                onClick: handleOpen
            }),
            /*#__PURE__*/ jsx(TravelerSelectionMobile, {
                ...TravelerSelectionProps,
                isOpen: isOpen,
                handleOpen: handleOpen,
                isDisabled: isTravelerSelectionDisabled
            })
        ]
    }) : /*#__PURE__*/ jsx(TravelerSelectionDesktop, {
        ...TravelerSelectionProps,
        isOpen: isOpen,
        renderInput: InputTrigger,
        handleOpen: handleOpen,
        collisionAvoidance: collisionAvoidance
    });
}
export { TravelerSelection };
